import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';

import { ChatConversationStateOptions, useChatConversationState } from './useChatConversationState';
import { ConversationSession } from '../../types/gateway-chat-types';

export interface UseChatConversationManagerOptions<TConversation>
  extends ChatConversationStateOptions<TConversation> {
  fetchConversations: () => Promise<TConversation[]>;
  createConversation: (name?: string) => Promise<TConversation>;
  renameConversation: (conversationId: string, conversationName: string) => Promise<TConversation>;
  onConversationActivated?: (conversationId: string) => Promise<void> | void;
  generateConversationName?: (index: number) => string;
}

export interface ChatConversationManagerState<TConversation> {
  conversations: TConversation[];
  activeConversationId: string;
  isLoadingConversations: boolean;
  isCreatingConversation: boolean;
  loadConversations: () => Promise<TConversation[]>;
  createConversation: (name?: string) => Promise<TConversation>;
  renameConversation: (conversationId: string, conversationName: string) => Promise<TConversation>;
  switchConversation: (conversationId: string) => Promise<void>;
  setActiveConversationId: (conversationId: string) => void;
  setConversations: Dispatch<SetStateAction<TConversation[]>>;
}

const defaultGenerateConversationName = (index: number) => `Chat ${index + 1}`;

export const useChatConversationManager = <TConversation extends ConversationSession>(
  options: UseChatConversationManagerOptions<TConversation>
): ChatConversationManagerState<TConversation> => {
  const {
    fetchConversations,
    createConversation: createConversationRequest,
    renameConversation: renameConversationRequest,
    onConversationActivated,
    generateConversationName = defaultGenerateConversationName,
    ...stateOptions
  } = options;

  const {
    conversations,
    setConversations,
    activeConversationId,
    setActiveConversationId,
    activeConversationRef,
    sortConversations,
    getConversationId,
  } = useChatConversationState<TConversation>(stateOptions);

  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);

  const loadConversations = useCallback(async () => {
    setIsLoadingConversations(true);
    try {
      const response = await fetchConversations();
      const sortedConversations = sortConversations(response);
      setConversations(sortedConversations);

      if (activeConversationRef.current) {
        const stillExists = sortedConversations.some(
          (conversation) => getConversationId(conversation) === activeConversationRef.current
        );
        if (!stillExists) {
          setActiveConversationId('');
        }
      }

      return sortedConversations;
    } finally {
      setIsLoadingConversations(false);
    }
  }, [
    activeConversationRef,
    fetchConversations,
    getConversationId,
    setActiveConversationId,
    setConversations,
    sortConversations,
  ]);

  const connectToConversation = useCallback(
    async (conversationId: string) => {
      if (!conversationId) {
        return;
      }

      await onConversationActivated?.(conversationId);
    },
    [onConversationActivated]
  );

  const createConversation = useCallback(
    async (name?: string) => {
      setIsCreatingConversation(true);
      try {
        const conversationName = name ?? generateConversationName(conversations.length);
        const newConversation = await createConversationRequest(conversationName);

        setConversations((prev) => {
          const withoutNew = prev.filter(
            (conversation) => getConversationId(conversation) !== getConversationId(newConversation)
          );
          return sortConversations([newConversation, ...withoutNew]);
        });

        const newConversationId = getConversationId(newConversation);
        setActiveConversationId(newConversationId);
        await connectToConversation(newConversationId);
        return newConversation;
      } finally {
        setIsCreatingConversation(false);
      }
    },
    [
      connectToConversation,
      conversations.length,
      createConversationRequest,
      generateConversationName,
      getConversationId,
      setActiveConversationId,
      setConversations,
      sortConversations,
    ]
  );

  const renameConversation = useCallback(
    async (conversationId: string, conversationName: string) => {
      const trimmedName = conversationName.trim();
      if (!trimmedName) {
        throw new Error('Conversation name is required');
      }

      const updatedConversation = await renameConversationRequest(conversationId, trimmedName);
      setConversations((prev) =>
        sortConversations(
          prev.map((conversation) =>
            getConversationId(conversation) === conversationId
              ? {
                  ...conversation,
                  conversation_name: updatedConversation.conversation_name,
                  last_activity: updatedConversation.last_activity,
                }
              : conversation
          )
        )
      );
      setActiveConversationId(conversationId);
      await connectToConversation(conversationId);

      return updatedConversation;
    },
    [
      connectToConversation,
      getConversationId,
      renameConversationRequest,
      setActiveConversationId,
      setConversations,
      sortConversations,
    ]
  );

  const switchConversation = useCallback(
    async (conversationId: string) => {
      if (!conversationId || conversationId === activeConversationRef.current) {
        return;
      }

      setActiveConversationId(conversationId);
      await connectToConversation(conversationId);
    },
    [activeConversationRef, connectToConversation, setActiveConversationId]
  );

  return useMemo(
    () => ({
      conversations,
      activeConversationId,
      isLoadingConversations,
      isCreatingConversation,
      loadConversations,
      createConversation,
      renameConversation,
      switchConversation,
      setActiveConversationId,
      setConversations,
    }),
    [
      activeConversationId,
      conversations,
      createConversation,
      isCreatingConversation,
      isLoadingConversations,
      loadConversations,
      renameConversation,
      setActiveConversationId,
      setConversations,
      switchConversation,
    ]
  );
};
